import { withoutTrailingSlash } from 'ufo'
import type { Ref } from 'vue'

import { breadcrumbJsonLd, jsonLdScript } from '../utils/jsonLd'

import type { CategoryDto } from '#Products/models/Pim/CategoryDto'

/**
 * Цепочка «корень → ... → категория» обходом вверх по первому родителю.
 * Защита от цикла — по числу известных категорий.
 */
function categoryChain(
  category: CategoryDto,
  byIds?: Record<string, CategoryDto>,
): CategoryDto[] {
  const chain: CategoryDto[] = [category]
  if (!byIds) return chain

  let current = category
  for (let i = 0; i < Object.keys(byIds).length; i++) {
    const parent = current.parentsIds?.[0] != null
      ? byIds[current.parentsIds[0]]
      : undefined
    if (!parent) break

    chain.unshift(parent)
    current = parent
  }

  return chain
}

/**
 * SEO-мета страницы категории/главной (SSR): title/description, Open Graph и
 * JSON-LD BreadcrumbList (по цепочке предков категории). Для главной (без
 * категории) — дефолт витрины; og:type наследуется из seoRoot-плагина.
 * Мета из админки (MR 9b) имеет приоритет над авто-генерацией — см. SeoService.
 */
export const useCategorySeo = (
  category: Ref<CategoryDto | undefined>,
  categoriesByIds?: Ref<Record<string, CategoryDto> | undefined>,
) => {
  const { service: seo } = useSeo()
  const { routing } = useProducts()
  const url = useRequestURL()
  // Пагинированный листинг (?page=N): «— страница N» делает title/description
  // уникальными в рамках цепочки, иначе вебмастерки жалуются на дубли
  const { page } = useListingPagination()

  const pageTitle = () => {
    const title = seo.categoryTitle(category.value)

    return page.value > 1
      ? `${title ?? 'Каталог'} — страница ${page.value}`
      : title
  }

  const pageDescription = () => {
    const description = seo.categoryDescription(category.value)

    return page.value > 1
      ? `${description} Страница ${page.value}.`
      : description
  }

  // og-картинку и JSON-LD считаем жадно в setup-контексте (см. useProductDetailsSeo)
  const ogImage = ref<string | undefined>()
  watchEffect(() => {
    ogImage.value = seo.categoryOgImage(category.value)
  })

  const jsonLdScripts = ref<ReturnType<typeof jsonLdScript>[]>([])
  watchEffect(() => {
    if (!category.value) {
      jsonLdScripts.value = []
      return
    }

    const chain = categoryChain(category.value, categoriesByIds?.value)

    jsonLdScripts.value = [
      jsonLdScript(breadcrumbJsonLd([
        { name: 'Главная', url: `${url.origin}/` },
        ...chain.map((item, index) => ({
          name: item.name,
          // последний элемент — текущая страница, URL можно опустить
          url: index < chain.length - 1
            ? withoutTrailingSlash(`${url.origin}${routing.getCatalog(item).route}`)
            : undefined,
        })),
      ])),
    ]
  })

  useSeoMeta({
    title: pageTitle,
    description: pageDescription,

    ogTitle: pageTitle,
    ogDescription: pageDescription,
    // og-картинка категории из админки; при её отсутствии — дефолт витрины
    // (та же величина, что ставит seoRoot-плагин)
    ogImage: () => ogImage.value,
  })

  useHead(computed(() => ({ script: jsonLdScripts.value })))
}
